import { useState, useRef, useEffect } from 'react';
import SubmodelView from './SubmodelView';
import { useIntl } from 'react-intl';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { isAuthenticated } from '../services/KeycloakService';
import { fetchSubmodel } from '../services/DppManagerService';
import { useLocation, useNavigate } from 'react-router-dom';
import Config from '../config/Config';
import getLocalizedText from './Utils';
import LoadingDots from './LoadingDots';
import '../css/DppSubmodelTabs.css';

function DppSubmodelTabs({ submodelDescriptors }) {
  const { mainColor, secondaryColor } = Config.appearance;
  const [activeKey, setActiveKey] = useState(null);
  const [submodels, setSubmodels] = useState({});
  const [loading, setLoading] = useState(null);
  const [isSticky, setIsSticky] = useState(false);
  const [tabsBarHeight, setTabsBarHeight] = useState(0);
  const tabListRef = useRef(null);
  const tabRefs = useRef([]);
  const sentinelRef = useRef(null);
  const wrapperRef = useRef(null);
  const intl = useIntl();
  const location = useLocation();
  const navigate = useNavigate();

  // Measure the tab bar height so the placeholder keeps layout stable
  useEffect(() => {
    if (wrapperRef.current) {
      setTabsBarHeight(wrapperRef.current.offsetHeight);
    }
  }, [submodelDescriptors]);

  // Detect sticky: watch the sentinel div just above the wrapper
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Only go sticky when the sentinel is above the viewport (scrolled past),
        // not on initial load when it's simply below the fold.
        setIsSticky(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      },
      { threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  const handleSelectTab = async (idShort, event) => {
    event.preventDefault();
    event.stopPropagation();

    setActiveKey(idShort);

    const params = new URLSearchParams(location.search);
    params.set('tab', idShort);
    navigate({ search: params.toString() }, { replace: true });

    scrollTabIntoView(idShort);

    const descriptor = submodelDescriptors.find((d) => d.idShort === idShort);
    if (descriptor && !submodels[idShort]) {
      setLoading(idShort);
      try {
        const data = await fetchSubmodel(descriptor.id);
        setSubmodels((prev) => ({ ...prev, [descriptor.idShort]: data }));
      } catch (err) {
        console.error('Submodel fetch failed:', err);
      } finally {
        setLoading(null);
      }
    }
  };

  useEffect(() => {
    if (!submodelDescriptors?.length || !isAuthenticated()) return;

    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    const defaultTab = submodelDescriptors[0].idShort;

    const initialTab =
      tabParam && submodelDescriptors.some((d) => d.idShort === tabParam)
        ? tabParam
        : defaultTab;

    setActiveKey(initialTab);
    scrollTabIntoView(initialTab);

    const descriptor = submodelDescriptors.find((d) => d.idShort === initialTab);
    if (descriptor && !submodels[initialTab]) {
      setLoading(initialTab);
      fetchSubmodel(descriptor.id)
        .then((data) => {
          setSubmodels((prev) => ({ ...prev, [initialTab]: data }));
        })
        .catch((err) => {
          console.error('Submodel fetch failed:', err);
        })
        .finally(() => {
          setLoading(null);
        });
    }
  }, [submodelDescriptors]);

  const scrollTabIntoView = (idShort) => {
    const index = submodelDescriptors.findIndex((d) => d.idShort === idShort);
    const el = tabRefs.current[index];
    const container = tabListRef.current;
    if (el && container) {
      const containerWidth = container.offsetWidth;
      const tabWidth = el.offsetWidth;
      const tabLeft = el.offsetLeft;
      const scrollLeft = tabLeft - (containerWidth - tabWidth) / 2;
      container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
    }
  };

  const scrollLeft = () => {
    const container = tabListRef.current;
    if (!container) return;
    container.scrollBy({ left: -(container.offsetWidth * 0.5), behavior: 'smooth' });
  };

  const scrollRight = () => {
    const container = tabListRef.current;
    if (!container) return;
    container.scrollBy({ left: container.offsetWidth * 0.5, behavior: 'smooth' });
  };

  const activeDescriptor = submodelDescriptors.find((d) => d.idShort === activeKey);
  const activeSubmodel = activeDescriptor ? submodels[activeDescriptor.idShort] : null;

  return (
    <div
      className="submodel-tabs-container"
      style={{
        '--dpp-main-color': mainColor,
        '--dpp-secondary-color': secondaryColor,
      }}
    >
      {/* Sentinel: when this scrolls out of view the tabs are stuck to top */}
      <div ref={sentinelRef} className="submodel-tabs-sentinel" aria-hidden="true" />

      {submodelDescriptors?.length > 0 && (
        <>
          {/* Placeholder keeps layout height when wrapper is position:fixed */}
          {isSticky && <div style={{ height: tabsBarHeight }} aria-hidden="true" />}

          <div
            ref={wrapperRef}
            className={`submodel-tabs-scroll-wrapper${isSticky ? ' is-sticky' : ''}`}
          >
            <div className="submodel-tabs-inner">
              <button onClick={scrollLeft} className="scroll-button left">
                <FaChevronLeft style={{ fontSize: '16px' }} />
              </button>

              <div className="scrollable-tabs-list" ref={tabListRef}>
                {submodelDescriptors.map((descriptor, index) => (
                  <div
                    key={descriptor.idShort}
                    className="scrollable-tab-item"
                    ref={(el) => (tabRefs.current[index] = el)}
                  >
                    <div
                      onClick={(e) => handleSelectTab(descriptor.idShort, e)}
                      className={`scrollable-tab-button ${activeKey === descriptor.idShort ? 'active' : 'inactive'}`}
                    >
                      <span>
                        {getLocalizedText(descriptor.displayName, descriptor.idShort, intl.locale)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <button onClick={scrollRight} className="scroll-button right">
                <FaChevronRight style={{ fontSize: '16px' }} />
              </button>
            </div>
          </div>
        </>
      )}

      <div className="submodel-tab-content" style={{ position: 'relative', minHeight: '300px' }}>
        {loading && (
          <div className="overlay-loading">
            <LoadingDots size={14} color={mainColor} />
          </div>
        )}
        {activeSubmodel && (
          <div className="submodel-tab-pane fade">
            <SubmodelView submodel={activeSubmodel} />
          </div>
        )}
      </div>
    </div>
  );
}

export default DppSubmodelTabs;